import { durationInDays, formatDate, formatDayMonth } from "@/modules/carga-trabalho";
import type {
  CargaTrabalhoIntervalo,
  CargaTrabalhoObra,
  ConflitoAlocacao,
} from "@/modules/carga-trabalho";
import type { Profissional, ProfissionaisPeriodo } from "@/modules/recursos";
import { textoDaObra } from "@/shared/utils/formatar-identificacao-obra";

/** O que o selo do passo 1 mostra: indisponível no período ou já alocado nas datas da alocação. */
export function substitutoIndisponivel(
  profissional: Pick<Profissional, "indisponibilidade" | "possuiAlocacaoNoPeriodo">,
): boolean {
  return (
    profissional.indisponibilidade.indisponivel || profissional.possuiAlocacaoNoPeriodo === true
  );
}

export function obraTitulo(
  obra: Pick<CargaTrabalhoObra, "codigo" | "nome" | "embarcacao">,
): string {
  return `Obra ${textoDaObra(obra)}`;
}

/** `DD/MM/AAAA - DD/MM/AAAA`. */
export function formatIntervalo(intervalo: CargaTrabalhoIntervalo): string {
  return `${formatDate(intervalo.inicio)} - ${formatDate(intervalo.fim)}`;
}

/** Período da Obra: a etapa Obra; sem ela, do início do planejamento ao fim do fechamento. */
export function periodoObra(obra: CargaTrabalhoObra): CargaTrabalhoIntervalo | null {
  const { planejamento, obra: execucao, fechamento } = obra.etapas;
  if (execucao) return execucao;
  const etapas = [planejamento, fechamento].filter(
    (etapa): etapa is CargaTrabalhoIntervalo => etapa !== null,
  );
  if (etapas.length === 0) return null;
  return {
    inicio: etapas.map((etapa) => etapa.inicio).sort()[0] ?? "",
    fim:
      etapas
        .map((etapa) => etapa.fim)
        .sort()
        .reverse()[0] ?? "",
  };
}

/** `DD/MM - DD/MM (N dias corridos)`, como no protótipo. */
export function descreverSobreposicao(conflito: ConflitoAlocacao): string {
  const dias = durationInDays(conflito);
  return `${formatDayMonth(conflito.inicio)} - ${formatDayMonth(conflito.fim)} (${dias} ${
    dias === 1 ? "dia corrido" : "dias corridos"
  })`;
}

/** Período Mês/Ano exigido pela listagem de profissionais, cobrindo o intervalo. */
export function toMonthRange(intervalo: CargaTrabalhoIntervalo): ProfissionaisPeriodo {
  return { from: intervalo.inicio.slice(0, 7), to: intervalo.fim.slice(0, 7) };
}

/** Intervalo que cobre as duas alocações do conflito. */
export function envelopeConflito(conflito: ConflitoAlocacao): CargaTrabalhoIntervalo {
  const { a, b } = conflito;
  return {
    inicio: a.inicio < b.inicio ? a.inicio : b.inicio,
    fim: a.fim > b.fim ? a.fim : b.fim,
  };
}

/** Conflito utilizável: duas alocações identificadas pelo bff (o id vai na requisição). */
export function isConflitoValido(
  conflito: ConflitoAlocacao | undefined,
): conflito is ConflitoAlocacao {
  return Boolean(conflito?.a.id && conflito.b.id && conflito.a.id !== conflito.b.id);
}

/** RN04 (HU 2040): só o papel da alocação é substituído, não a equipe toda. */
export function notaDoPapel(papel: string): string {
  return `Apenas o ${papel} será substituído nesta obra.`;
}
